import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { hasUnseenWhatsNew } from '../../data/whatsNew';

interface SidebarSession {
  id: string;
  title: string;
  modelDisplayName: string;
  updatedAt: string;
}

interface SidebarProps {
  sessions: SidebarSession[];
  onNewChat: () => void;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  onRenameSession: (id: string, title: string) => void;
  isOpen: boolean;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function Sidebar({ sessions, onNewChat, onSelectSession, onDeleteSession, onRenameSession, isOpen }: SidebarProps) {
  const { sessionId: activeId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Show an "unread" dot on the What's New link until the user opens it.
  const [showWhatsNewDot, setShowWhatsNewDot] = useState(() => hasUnseenWhatsNew());
  useEffect(() => {
    setShowWhatsNewDot(hasUnseenWhatsNew());
  }, [location.pathname]);

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const startRename = (session: SidebarSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingId(session.id);
    setRenameValue(session.title);
  };

  const submitRename = (sessionId: string) => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== sessions.find((s) => s.id === sessionId)?.title) {
      onRenameSession(sessionId, trimmed);
    }
    setRenamingId(null);
  };

  const handleDelete = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteId(sessionId);
  };

  const confirmDelete = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteSession(sessionId);
    setConfirmDeleteId(null);
  };

  const cancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  return (
    <aside className={`fixed left-0 top-14 bottom-0 z-20 flex w-64 flex-col bg-gradient-sidebar transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      {/* Right border */}
      <div className="absolute right-0 top-0 bottom-0 w-px bg-border-light" />

      {/* New Chat button */}
      <div className="p-3">
        <button
          type="button"
          onClick={onNewChat}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary to-sky-400 px-4 py-2.5 font-body text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5 active:translate-y-0"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Chat
        </button>
      </div>

      {/* Session list */}
      <nav className="flex-1 overflow-y-auto px-2">
        <ul className="space-y-0.5">
          {sessions.map((session) => {
            const isActive = session.id === activeId;
            const isRenaming = renamingId === session.id;
            return (
              <li key={session.id} className="group relative">
                <button
                  type="button"
                  onClick={() => !isRenaming && onSelectSession(session.id)}
                  className={[
                    'w-full rounded-xl px-3 py-2.5 text-left transition-all',
                    isActive
                      ? 'bg-primary/10 text-text-primary shadow-sm'
                      : 'text-text-secondary hover:bg-bg-tertiary/60',
                  ].join(' ')}
                >
                  {/* Active indicator bar */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary" />
                  )}

                  {isRenaming ? (
                    <input
                      ref={renameInputRef}
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitRename(session.id);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      onBlur={() => submitRename(session.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full rounded-lg border border-primary/30 bg-bg px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      maxLength={100}
                    />
                  ) : (
                    <div className="truncate pr-12 text-sm font-medium">
                      {session.title || 'Untitled Chat'}
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-2 text-xs text-text-muted">
                    <span className="truncate">{session.modelDisplayName}</span>
                    <span className="shrink-0 opacity-70">{formatDate(session.updatedAt)}</span>
                  </div>
                </button>

                {/* Inline delete confirmation */}
                {confirmDeleteId === session.id && (
                  <div className="absolute right-2 top-2.5 flex items-center gap-1 rounded-lg bg-bg px-1.5 py-0.5 shadow-sm border border-border-light">
                    <span className="text-xs text-text-muted">Delete?</span>
                    <button
                      type="button"
                      onClick={(e) => confirmDelete(session.id, e)}
                      className="rounded px-1.5 py-0.5 text-xs font-medium text-error hover:bg-error/10"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={cancelDelete}
                      className="rounded px-1.5 py-0.5 text-xs font-medium text-text-muted hover:bg-bg-tertiary"
                    >
                      No
                    </button>
                  </div>
                )}

                {/* Action icons — visible on hover */}
                {!isRenaming && confirmDeleteId !== session.id && (
                  <div className="absolute right-2 top-2.5 hidden items-center gap-0.5 group-hover:flex">
                    <button
                      type="button"
                      onClick={(e) => startRename(session, e)}
                      className="rounded-lg p-1.5 text-text-muted transition-all hover:bg-primary/10 hover:text-primary"
                      title="Rename"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(session.id, e)}
                      className="rounded-lg p-1.5 text-text-muted transition-all hover:bg-error/10 hover:text-error"
                      title="Delete"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {sessions.length === 0 && (
          <div className="px-3 py-8 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-text-secondary">No conversations yet</p>
            <p className="mt-1 text-xs text-text-muted">Start a new chat to begin</p>
          </div>
        )}
      </nav>

      {/* Bottom links */}
      <div className="border-t border-border-light p-2 space-y-0.5">
        <button
          type="button"
          onClick={() => navigate('/history')}
          className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm text-text-secondary transition-all hover:bg-primary/10 hover:text-primary"
        >
          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          History
        </button>
        <button
          type="button"
          onClick={() => navigate('/whats-new')}
          className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm text-text-secondary transition-all hover:bg-primary/10 hover:text-primary"
        >
          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.456-2.456L14.25 6l1.035-.259a3.375 3.375 0 002.456-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
          </svg>
          <span className="flex-1">What’s New</span>
          {showWhatsNewDot && (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-accent"
              aria-label="New updates available"
            />
          )}
        </button>
        {user?.isAdmin && (
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm text-text-secondary transition-all hover:bg-primary/10 hover:text-primary"
          >
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
            Admin
          </button>
        )}
      </div>
    </aside>
  );
}
